import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import type { LotAnimalEbvFilteredRequestInput } from '@/graphqlClient/types';
import type { GetLotAnimalEbvsQuery } from '@/publicSearch/graphql/queries.generated';
import type { AnimalDetailData } from '@/components/publicSearch/animal/AnimalDetailFields';
import {
  resolveSaleLotDisplayName,
  sanitizeSaleLotIdent,
} from './resolveSaleLotDisplayName';

export type SaleLotIdentity = { saleId: string; lotNo: string };
export type SaleLotDetail =
  GetLotAnimalEbvsQuery['lotAnimalEbvs']['items'][number];

export function isValidSaleLotIdentity({ saleId, lotNo }: SaleLotIdentity) {
  return (
    Number.isInteger(Number(saleId)) &&
    Number(saleId) > 0 &&
    Boolean(lotNo.trim()) &&
    lotNo.length <= 16
  );
}

export function buildSaleLotQueryOptions(
  identity: SaleLotIdentity,
  orgId?: string | number | null,
) {
  return {
    skip: !orgId || !isValidSaleLotIdentity(identity),
    variables: {
      requestInput: {
        pagination: { currentPage: 1, pageSize: 1 },
        filterOptions: [
          { field: 'SALE_ID', operator: 'EQUALS', value: identity.saleId },
          { field: 'LOT_NO', operator: 'EQUALS', value: identity.lotNo },
        ],
      } satisfies LotAnimalEbvFilteredRequestInput,
    },
    context: { headers: setDefaultHeader(orgId) },
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  };
}

/** Supply the standard detail renderer with lot data without inventing an animal. */
export function toSaleLotDetailData(lot: SaleLotDetail): AnimalDetailData {
  return {
    animalId: lot.animalId && lot.animalId > 0 ? lot.animalId : null,
    animalIdent: sanitizeSaleLotIdent(lot.animalIdent) || null,
    animalName: resolveSaleLotDisplayName(lot.lotAnimalName, lot.animalName),
    animalStatus: lot.animalStatus ?? null,
    dateOfBirth: lot.dateOfBirth ?? null,
    registrationStatus: lot.registrationStatus ?? null,
    sex: lot.sex ?? null,
    tattoo: lot.animalTattoo ?? null,
    sire:
      lot.sireAnimalId && lot.sireAnimalId > 0
        ? { animalId: lot.sireAnimalId, animalName: lot.sireAnimalName ?? null }
        : undefined,
    dam:
      lot.damAnimalId && lot.damAnimalId > 0
        ? { animalId: lot.damAnimalId, animalName: lot.damAnimalName ?? null }
        : undefined,
    ebvRaw: lot,
  };
}
